import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { subscriptionService } from '../services/api';
import toast from 'react-hot-toast';
import SearchBar from '../components/SearchBar';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { splitPhoneNumber } from '../utils/splitPhoneNumber';
import SubscriptionKeywordsInput from '../components/SubscriptionKeywordsInput';

// Tag options based on SubscriptionTagOptions enum
const TAG_OPTIONS = [
  { value: 'emergency only', label: 'Emergency Only' },
  { value: 'testing', label: 'Testing' },
  { value: 'portal_only', label: 'Portal Only' }
];

const emptyFormData = () => ({
  target: '',
  description: '',
  type: 'whatsapp',
  tags: [],
  keywords: [],
  meta_data: {},
  countryCode: '+852',
  phoneNumber: ''
});

const Subscriptions = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [formData, setFormData] = useState(emptyFormData());

  const clid = user?.clid || 'CLID0000';

  useEffect(() => {
    fetchSubscriptions();
  }, [clid]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getSubscriptions(clid, 100, 0);
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Prepare subscription data
      const subscriptionData = { ...formData };

      // For WhatsApp subscriptions, combine country code and phone number
      if (formData.type === 'whatsapp') {
        subscriptionData.target = formData.countryCode + formData.phoneNumber;
        // Remove phone number fields from the data sent to API
        delete subscriptionData.countryCode;
        delete subscriptionData.phoneNumber;
      }

      await subscriptionService.createSubscription(clid, subscriptionData);
      toast.success('Subscription created successfully');
      setShowCreateDialog(false);
      setFormData(emptyFormData());
      fetchSubscriptions();
    } catch (error) {
      console.error('Error creating subscription:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create subscription';
      toast.error(errorMessage);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Prepare subscription data
      const subscriptionData = { ...formData };

      // For WhatsApp subscriptions, combine country code and phone number
      if (formData.type === 'whatsapp') {
        subscriptionData.target = formData.countryCode + formData.phoneNumber;
        // Remove phone number fields from the data sent to API
        delete subscriptionData.countryCode;
        delete subscriptionData.phoneNumber;
      }

      await subscriptionService.updateSubscription(editingSubscription.id, clid, subscriptionData);
      toast.success('Subscription updated successfully');
      setShowEditDialog(false);
      setEditingSubscription(null);
      setFormData(emptyFormData());
      fetchSubscriptions();
    } catch (error) {
      console.error('Error updating subscription:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update subscription';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      try {
        await subscriptionService.deleteSubscription(subscriptionId);
        toast.success('Subscription deleted successfully');
        fetchSubscriptions();
      } catch (error) {
        console.error('Error deleting subscription:', error);
        // Extract error message from API response
        const errorMessage = error.response?.data?.error || error.message || 'Failed to delete subscription';
        toast.error(errorMessage);
      }
    }
  };

  const handleToggleActive = async (subscription) => {
    const newStatus = !subscription.is_active;
    const action = newStatus ? 'activate' : 'deactivate';

    if (!newStatus) {
      // Show confirmation for deactivation
      if (!window.confirm(`Are you sure you want to deactivate this subscription for "${subscription.target}"?`)) {
        return;
      }
    }

    try {
      await subscriptionService.updateSubscription(subscription.id, clid, {
        ...subscription,
        is_active: newStatus
      });
      toast.success(`Subscription ${action}d successfully`);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error toggling subscription status:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update subscription status';
      toast.error(errorMessage);
    }
  };

  // Handle country code change
  const handleCountryCodeChange = (value) => {
    setFormData({ ...formData, countryCode: value });
  };

  // Handle phone number change
  const handlePhoneNumberChange = (value) => {
    setFormData({ ...formData, phoneNumber: value });
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);

    // Parse phone number if it's a WhatsApp subscription
    let countryCode = '+852';
    let phoneNumber = '';

    if (subscription.type === 'whatsapp' && subscription.target) {
      // Try to extract country code and phone number from target
      const split = splitPhoneNumber(subscription.target);
      if (split.phoneNumber) {
        countryCode = split.countryCode;
        phoneNumber = split.phoneNumber;
      } else {
        // If no country code found, treat as phone number only
        phoneNumber = subscription.target.replace(/\D/g, '');
      }
    }

    setFormData({
      target: subscription.target,
      description: subscription.description || '',
      type: subscription.type,
      tags: subscription.tags || [],
      keywords: subscription.keywords || [],
      meta_data: subscription.meta_data || {},
      countryCode: countryCode,
      phoneNumber: phoneNumber
    });
    setShowEditDialog(true);
  };

  const filteredSubscriptions = subscriptions.filter(subscription =>
    subscription.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subscription.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <button
            onClick={() => {
              console.log('Add Subscription button clicked');
              setShowCreateDialog(true);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium text-sm border border-blue-600"
            style={{ minWidth: '140px', zIndex: 10 }}
          >
            + Add Subscription
          </button>
        </div>

        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search subscriptions..."
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Keywords
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {subscription.target}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {subscription.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {subscription.description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {subscription.tags && subscription.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {subscription.tags.map(tag => {
                          const getTagColor = (tagValue) => {
                            if (tagValue === 'emergency only' || tagValue === 'emergency') return 'bg-red-100 text-red-800';
                            if (tagValue === 'portal_only') return 'bg-purple-100 text-purple-800';
                            return 'bg-blue-100 text-blue-800';
                          };
                          const tagLabel = TAG_OPTIONS.find(opt => opt.value === tag)?.label || tag;
                          return (
                            <span key={tag} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagColor(tag)}`}>
                              {tagLabel}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {subscription.keywords && subscription.keywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {subscription.keywords.map(keyword => (
                          <span key={keyword} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                            {keyword}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">All voicemails</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      subscription.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {subscription.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(subscription.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(subscription)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(subscription)}
                        className={`${
                          subscription.is_active
                            ? 'text-orange-600 hover:text-orange-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        {subscription.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredSubscriptions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No subscriptions found</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Subscription</h3>
                <button
                  onClick={() => setShowCreateDialog(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  {formData.type === 'whatsapp' ? (
                    <PhoneNumberInput
                      countryCode={formData.countryCode}
                      phoneNumber={formData.phoneNumber}
                      onCountryCodeChange={handleCountryCodeChange}
                      onPhoneNumberChange={handlePhoneNumberChange}
                      label="Phone Number"
                      required={true}
                    />
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target
                      </label>
                      <input
                        type="text"
                        value={formData.target}
                        onChange={(e) => setFormData({...formData, target: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      />
                    </>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows="3"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_OPTIONS.map(tagOption => (
                      <label key={tagOption.value} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags.includes(tagOption.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, tags: [...formData.tags, tagOption.value]});
                            } else {
                              setFormData({...formData, tags: formData.tags.filter(tag => tag !== tagOption.value)});
                            }
                          }}
                          className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{tagOption.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voicemail Keywords
                  </label>
                  <SubscriptionKeywordsInput
                    value={formData.keywords || []}
                    onChange={(keywords) => setFormData({ ...formData, keywords })}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If set, only receive voicemail alerts when the transcript contains any keyword. Leave empty for all voicemails.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateDialog(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEditDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Subscription</h3>
                <button
                  onClick={() => setShowEditDialog(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleUpdate}>
                <div className="mb-4">
                  {formData.type === 'whatsapp' ? (
                    <PhoneNumberInput
                      countryCode={formData.countryCode}
                      phoneNumber={formData.phoneNumber}
                      onCountryCodeChange={handleCountryCodeChange}
                      onPhoneNumberChange={handlePhoneNumberChange}
                      label="Phone Number"
                      required={true}
                    />
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target
                      </label>
                      <input
                        type="text"
                        value={formData.target}
                        onChange={(e) => setFormData({...formData, target: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      />
                    </>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="webhook">Webhook</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows="3"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_OPTIONS.map(tagOption => (
                      <label key={tagOption.value} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags.includes(tagOption.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, tags: [...formData.tags, tagOption.value]});
                            } else {
                              setFormData({...formData, tags: formData.tags.filter(tag => tag !== tagOption.value)});
                            }
                          }}
                          className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{tagOption.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voicemail Keywords
                  </label>
                  <SubscriptionKeywordsInput
                    value={formData.keywords || []}
                    onChange={(keywords) => setFormData({ ...formData, keywords })}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If set, only receive voicemail alerts when the transcript contains any keyword. Leave empty for all voicemails.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditDialog(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
